import { useCallback, useEffect, useState } from "react";
import { Eye, FlaskConical, Image as ImageIcon, Loader2, Play, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, mediaUrl } from "@/lib/api";
import { AdminButton, AdminCard, AdminField, EmptyState, adminInput } from "@/components/admin/ui";

const STATUSES = [
  ["draft", "Bozze"],
  ["published", "Pubblicati"],
  ["scheduled", "Programmati"],
  ["archived", "Archivio"],
];

const CLUSTERS = {
  "borghi-territorio": "Borghi e territorio",
  enogastronomia: "Enogastronomia",
  "vino-vigne": "Vino e vigne",
  "esperienze-irpinia": "Esperienze in Irpinia",
  stagioni: "Stagioni",
  "castagne-castagneti": "Castagne e castagneti dell'Irpinia",
  "funghi-porcini": "Funghi porcini e boschi dell'Irpinia",
  "progetto-aterrana": "Dal progetto Aterrana",
  "idee-per-il-borgo": "Idee per il Borgo",
};

function fmtDateTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("it-IT", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function Brief({ a }) {
  if (!a.primary_keyword && !a.reason) return null;
  return (
    <div className="border border-sage/40 bg-sage/5 p-4 text-sm" data-testid="seo-brief">
      <p className="eyebrow text-sage">SEO brief</p>
      <dl className="mt-3 space-y-1.5 text-foreground/75">
        <div><dt className="inline text-wood/60">Keyword primaria: </dt><dd className="inline">{a.primary_keyword || "—"}</dd></div>
        <div><dt className="inline text-wood/60">Keyword secondarie: </dt><dd className="inline">{(a.secondary_keywords || []).join(", ") || "—"}</dd></div>
        <div><dt className="inline text-wood/60">Long-tail: </dt><dd className="inline">{(a.long_tail || []).join(", ") || "—"}</dd></div>
        <div><dt className="inline text-wood/60">Intento: </dt><dd className="inline">{a.intent || "—"}</dd></div>
        <div><dt className="inline text-wood/60">Cluster: </dt><dd className="inline">{a.category_name}</dd></div>
        <div><dt className="inline text-wood/60">Stagionalità: </dt><dd className="inline">{a.seasonality || "—"}</dd></div>
        <div><dt className="inline text-wood/60">Motivo: </dt><dd className="inline">{a.reason || "—"}</dd></div>
      </dl>
      {a.sources?.length > 0 && (
        <div className="mt-4">
          <p className="eyebrow text-sage">Fonti utilizzate</p>
          <ul className="mt-2 space-y-1">
            {a.sources.map((s) => (
              <li key={s.url}>
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-sage underline underline-offset-4 break-all">
                  {s.title || s.url}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Science({ a }) {
  const fc = a.fact_check || {};
  if (!(a.scientific_sources?.length || fc.checked_at)) return null;
  return (
    <div className="border border-wood/25 bg-beige/40 p-4 text-sm" data-testid="scientific-sources">
      <p className="eyebrow text-wood">Fonti scientifiche</p>
      {a.evidence_level && (
        <p className="mt-2 text-xs text-foreground/60">
          Livello di evidenza complessivo (uso interno): <strong>{a.evidence_level}</strong>
        </p>
      )}
      {a.scientific_sources?.length > 0 ? (
        <ul className="mt-3 space-y-3">
          {a.scientific_sources.map((s, i) => (
            <li key={s.url || i} className="border-t border-wood/15 pt-3">
              <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-sage underline underline-offset-4 break-all">
                {s.title || s.url}
              </a>
              <p className="mt-1 text-xs text-foreground/60">
                {[s.authors, s.journal, s.year].filter(Boolean).join(" · ")}
              </p>
              <p className="mt-1 text-xs text-foreground/60">
                Tipo di evidenza: {s.evidence_type || "—"} · Livello: {s.evidence_level || "—"}
                {s.doi ? ` · DOI: ${s.doi}` : ""}
              </p>
              {s.claim && <p className="mt-1 text-xs text-foreground/70">Sostiene: {s.claim}</p>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-foreground/60">Nessuna fonte scientifica associata.</p>
      )}
      {(fc.factual_changes?.length > 0 || fc.scientific_changes?.length > 0 || fc.removed_claims?.length > 0) && (
        <div className="mt-4 border-t border-wood/15 pt-3 text-xs text-foreground/65 space-y-2">
          {fc.factual_changes?.length > 0 && <p>Fact-check fattuale: {fc.factual_changes.join(" · ")}</p>}
          {fc.scientific_changes?.length > 0 && <p>Fact-check scientifico: {fc.scientific_changes.join(" · ")}</p>}
          {fc.removed_claims?.length > 0 && <p>Affermazioni rimosse: {fc.removed_claims.join(" · ")}</p>}
        </div>
      )}
    </div>
  );
}

function ImagePanel({ a, value, onChange, onSearch }) {
  const m = a.image_meta || {};
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/admin/uploads", body);
      onChange(data.url || data.path);
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(apiError(err, "Caricamento dell'immagine non riuscito."));
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="border border-wood/25 bg-beige/40 p-4 text-sm" data-testid="article-image-panel">
      <p className="eyebrow text-wood">Immagine articolo</p>
      {value ? (
        <img src={`${mediaUrl(value)}?w=768`} alt={a.hero_alt || ""} className="mt-3 max-h-56 border border-wood/15" />
      ) : (
        <p className="mt-3 text-foreground/60" data-testid="article-image-missing">
          Immagine da aggiungere — l'agente non ha trovato una fotografia con licenza verificabile.
        </p>
      )}
      {value && m.source_url && (
        <dl className="mt-3 space-y-1 text-xs text-foreground/70">
          <div><dt className="inline text-wood/60">Origine: </dt><dd className="inline">{m.provider || "—"}</dd></div>
          <div><dt className="inline text-wood/60">Autore: </dt><dd className="inline">{m.author || "non indicato"}</dd></div>
          <div>
            <dt className="inline text-wood/60">Licenza: </dt>
            <dd className="inline">
              {m.license_url ? (
                <a href={m.license_url} target="_blank" rel="noopener noreferrer" className="text-sage underline underline-offset-4">{m.license || "—"}</a>
              ) : (m.license || "—")}
            </dd>
          </div>
          <div><dt className="inline text-wood/60">Attribuzione richiesta: </dt><dd className="inline">{m.attribution_required ? "Sì" : "No"}</dd></div>
          <div>
            <dt className="inline text-wood/60">Pagina della fonte: </dt>
            <dd className="inline">
              <a href={m.source_url} target="_blank" rel="noopener noreferrer" className="text-sage underline underline-offset-4 break-all">{m.source_url}</a>
            </dd>
          </div>
          {m.original_url && (
            <div>
              <dt className="inline text-wood/60">File originale: </dt>
              <dd className="inline">
                <a href={m.original_url} target="_blank" rel="noopener noreferrer" className="text-sage underline underline-offset-4 break-all">{m.original_url}</a>
              </dd>
            </div>
          )}
          <div><dt className="inline text-wood/60">Data acquisizione: </dt><dd className="inline">{m.accessed_at ? new Date(m.accessed_at).toLocaleDateString("it-IT") : "—"}</dd></div>
        </dl>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {!value && (
          <AdminButton variant="outline" onClick={onSearch} data-testid="article-image-search">
            <ImageIcon size={13} /> Cerca un'immagine con licenza libera
          </AdminButton>
        )}
        <label className="inline-flex cursor-pointer items-center gap-2 border border-wood/30 px-4 py-2.5 text-[0.65rem] uppercase tracking-[0.16em] text-wood hover:bg-wood hover:text-cream transition-colors duration-400">
          <ImageIcon size={13} /> {uploading ? "Caricamento…" : "Carica un'altra immagine"}
          <input type="file" accept="image/*" className="hidden" data-testid="article-image-upload" disabled={uploading}
            onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
        </label>
        {value && (
          <AdminButton variant="danger" onClick={() => onChange("")} data-testid="article-image-remove">
            Rimuovi immagine
          </AdminButton>
        )}
      </div>
    </div>
  );
}

function Editor({ article, onSaved, onCancel, onEnrich, onImageSearch }) {
  const [f, setF] = useState({ ...article, tags: (article.tags || []).join(", "), secondary_keywords: (article.secondary_keywords || []).join(", ") });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async (status) => {
    setBusy(true);
    setError("");
    const payload = {
      title: f.title,
      slug: f.slug || "",
      excerpt: f.excerpt || "",
      body_markdown: f.body_markdown || "",
      cluster: f.cluster || "borghi-territorio",
      tags: f.tags ? f.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
      hero_image: f.hero_image || null,
      hero_alt: f.hero_alt || "",
      image_credit: f.image_credit || "",
      image_meta: f.hero_image === article.hero_image ? article.image_meta || {} : null,
      seo_title: f.seo_title || "",
      meta_description: f.meta_description || "",
      primary_keyword: f.primary_keyword || "",
      secondary_keywords: f.secondary_keywords ? f.secondary_keywords.split(",").map((t) => t.trim()).filter(Boolean) : [],
      canonical: f.canonical || "",
      cta: !!f.cta,
      status: status || f.status,
      published_at: status && status !== "published" ? null : f.published_at || null,
    };
    try {
      if (article.id) await api.put(`/admin/articles/${article.id}`, payload);
      else await api.post("/admin/articles", payload);
      toast.success(
        status === "published"
          ? "Articolo pubblicato"
          : status === "draft"
            ? "Salvato come bozza"
            : "Articolo aggiornato",
      );
      onSaved(status);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminCard title={article.id ? "Modifica articolo" : "Nuovo articolo"} testId="article-editor">
      <div className="grid gap-4 lg:grid-cols-3">
        <AdminField label="Titolo" className="lg:col-span-2">
          <input value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} className={adminInput} data-testid="article-title" />
        </AdminField>
        <AdminField label="Slug">
          <input value={f.slug || ""} onChange={(e) => setF({ ...f, slug: e.target.value })} className={adminInput} data-testid="article-slug" />
        </AdminField>
        <AdminField label="Categoria">
          <select value={f.cluster || "borghi-territorio"} onChange={(e) => setF({ ...f, cluster: e.target.value })} className={adminInput}>
            {Object.entries(CLUSTERS).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
          </select>
        </AdminField>
        <AdminField label="Tag (virgole)">
          <input value={f.tags || ""} onChange={(e) => setF({ ...f, tags: e.target.value })} className={adminInput} />
        </AdminField>
        <AdminField label="Data pubblicazione (ISO, opzionale)">
          <input value={f.published_at || ""} onChange={(e) => setF({ ...f, published_at: e.target.value })} className={adminInput} placeholder="2026-08-25T10:00:00+00:00" />
        </AdminField>
        <AdminField label="Estratto" className="lg:col-span-3">
          <textarea rows={2} value={f.excerpt || ""} onChange={(e) => setF({ ...f, excerpt: e.target.value })} className={adminInput} />
        </AdminField>
        <AdminField label="Testo (markdown)" className="lg:col-span-3">
          <textarea rows={18} value={f.body_markdown || ""} onChange={(e) => setF({ ...f, body_markdown: e.target.value })} className={`${adminInput} font-mono text-xs`} data-testid="article-body" />
        </AdminField>
        <AdminField label="SEO title">
          <input value={f.seo_title || ""} onChange={(e) => setF({ ...f, seo_title: e.target.value })} className={adminInput} />
        </AdminField>
        <AdminField label="Meta description" className="lg:col-span-2">
          <input value={f.meta_description || ""} onChange={(e) => setF({ ...f, meta_description: e.target.value })} className={adminInput} />
        </AdminField>
        <AdminField label="Keyword primaria">
          <input value={f.primary_keyword || ""} onChange={(e) => setF({ ...f, primary_keyword: e.target.value })} className={adminInput} />
        </AdminField>
        <AdminField label="Keyword secondarie (virgole)">
          <input value={f.secondary_keywords || ""} onChange={(e) => setF({ ...f, secondary_keywords: e.target.value })} className={adminInput} />
        </AdminField>
        <AdminField label="Canonical (opzionale)">
          <input value={f.canonical || ""} onChange={(e) => setF({ ...f, canonical: e.target.value })} className={adminInput} />
        </AdminField>
        <AdminField label="Immagine (percorso, opzionale)">
          <input value={f.hero_image || ""} onChange={(e) => setF({ ...f, hero_image: e.target.value })} className={adminInput} placeholder="osteria-aterrana/uploads/..." />
        </AdminField>
        <AdminField label="Alt immagine" className="lg:col-span-2">
          <input value={f.hero_alt || ""} onChange={(e) => setF({ ...f, hero_alt: e.target.value })} className={adminInput} />
        </AdminField>
        <AdminField label="Crediti immagine (mostrati sotto la foto)" className="lg:col-span-3">
          <input value={f.image_credit || ""} onChange={(e) => setF({ ...f, image_credit: e.target.value })} className={adminInput} data-testid="article-image-credit" />
        </AdminField>
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <ImagePanel
          a={article}
          value={f.hero_image || ""}
          onChange={(v) => setF({ ...f, hero_image: v })}
          onSearch={() => onImageSearch(article.id)}
        />
        <Science a={article} />
      </div>
      <label className="mt-4 flex items-center gap-2 text-sm text-foreground/70">
        <input type="checkbox" checked={!!f.cta} onChange={(e) => setF({ ...f, cta: e.target.checked })} className="h-4 w-4 accent-[hsl(var(--sage))]" />
        Mostra la chiusura con il richiamo all'osteria
      </label>
      {error && <p className="mt-4 border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">{error}</p>}
      <div className="mt-5 flex flex-wrap gap-2">
        <AdminButton onClick={() => save("draft")} disabled={busy} data-testid="article-save-draft">
          {busy && <Loader2 size={13} className="animate-spin" />} Salva bozza
        </AdminButton>
        <AdminButton variant="wood" onClick={() => save("published")} disabled={busy} data-testid="article-publish">
          Pubblica
        </AdminButton>
        {article.status === "published" && (
          <AdminButton variant="outline" onClick={() => save("draft")} disabled={busy} data-testid="article-unpublish">
            Riporta in bozza
          </AdminButton>
        )}
        {article.slug && article.status === "published" && (
          <a href={`/diario-dal-borgo/${article.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-4 py-2.5 text-[0.65rem] uppercase tracking-[0.16em] text-wood/70 hover:text-sage">
            Vedi online
          </a>
        )}
        {article.id && (
          <a
            href={`/admin/diario/anteprima/${article.id}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="article-preview"
            className="inline-flex items-center gap-2 border border-wood/30 px-4 py-2.5 text-[0.65rem] uppercase tracking-[0.16em] text-wood hover:bg-wood hover:text-cream transition-colors duration-400"
          >
            <Eye size={13} /> Anteprima
          </a>
        )}
        {article.id && (
          <AdminButton variant="outline" onClick={() => onEnrich(article.id)} disabled={busy} data-testid="article-enrich">
            <FlaskConical size={13} /> Arricchisci con la scienza + fact-check
          </AdminButton>
        )}
        <AdminButton variant="ghost" onClick={onCancel}>Chiudi</AdminButton>
      </div>
      <div className="mt-6">
        <Brief a={article} />
      </div>
    </AdminCard>
  );
}

export default function AdminArticles() {
  const [tab, setTab] = useState("published");
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [runs, setRuns] = useState([]);
  const [agent, setAgent] = useState(null);
  const [error, setError] = useState("");
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const [list, hist, cfg] = await Promise.all([
        api.get("/admin/articles", { params: { status: tab } }),
        api.get("/admin/articles/agent/runs"),
        api.get("/admin/articles/agent/settings"),
      ]);
      setRows(list.data);
      setRuns(hist.data);
      setAgent(cfg.data);
    } catch (err) {
      setError(apiError(err));
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    setError("");
    try {
      await api.post("/admin/articles/agent/run");
      setTab("draft");
      for (let i = 0; i < 60; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 5000));
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.get("/admin/articles/agent/runs");
        setRuns(data);
        if (data[0]?.status !== "running") break;
      }
      await load();
    } catch (err) {
      setError(apiError(err));
      await load();
    } finally {
      setRunning(false);
    }
  };

  const enrich = async (id) => {
    setRunning(true);
    setError("");
    try {
      await api.post(`/admin/articles/${id}/enrich`);
      toast.info("Ricerca scientifica e fact-check avviati: ci vogliono un paio di minuti.");
      for (let i = 0; i < 48; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 5000));
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.get("/admin/articles/agent/runs");
        setRuns(data);
        if (data[0]?.status !== "running") break;
      }
      const { data: fresh } = await api.get(`/admin/articles/${id}`);
      setEditing(fresh);
      await load();
      toast.success("Articolo verificato e aggiornato");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setRunning(false);
    }
  };

  const imageSearch = async (id) => {
    if (!id) return;
    setRunning(true);
    setError("");
    try {
      await api.post(`/admin/articles/${id}/image-search`);
      toast.info("Cerco una fotografia con licenza libera: un minuto circa.");
      for (let i = 0; i < 24; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 5000));
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.get("/admin/articles/agent/runs");
        setRuns(data);
        if (data[0]?.status !== "running") break;
      }
      const { data: fresh } = await api.get(`/admin/articles/${id}`);
      setEditing(fresh);
      await load();
      toast[fresh.hero_image ? "success" : "warning"](
        fresh.hero_image ? "Immagine trovata e allegata" : "Nessuna immagine sicura trovata: articolo senza foto",
      );
    } catch (err) {
      setError(apiError(err));
    } finally {
      setRunning(false);
    }
  };

  const saveAgent = async (patch) => {
    try {
      const { data } = await api.put("/admin/articles/agent/settings", { ...agent, ...patch });
      setAgent(data);
    } catch (err) {
      setError(apiError(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/articles/${id}`);
      load();
    } catch (err) {
      setError(apiError(err));
    }
  };

  // Link "Apri la bozza" dell'email di notifica: /admin/diario?articolo=<id>
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("articolo");
    if (!id) return;
    api
      .get(`/admin/articles/${id}`)
      .then(({ data }) => {
        setEditing(data);
        setTab(data.status || "draft");
      })
      .catch(() => setError("Articolo della notifica non trovato."));
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-articles">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[0.62rem] uppercase tracking-[0.2em] text-sage">Diario dal Borgo</p>
          <h1 className="mt-2 font-serif text-3xl md:text-4xl text-wood">Articoli</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <AdminButton variant="outline" onClick={() => setEditing({ status: "draft", cluster: "borghi-territorio", cta: true })} data-testid="article-new">
            Nuovo articolo
          </AdminButton>
          <AdminButton variant="wood" onClick={runNow} disabled={running} data-testid="agent-run-now">
            {running ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
            {running ? "Ricerca in corso…" : "Esegui ora"}
          </AdminButton>
        </div>
      </div>

      {error && <p className="border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive" data-testid="articles-error">{error}</p>}

      {agent && (
        <AdminCard title="Impostazioni SEO agent" testId="agent-settings">
          <div className="grid gap-4 md:grid-cols-4">
            <AdminField label="Agente attivo">
              <select value={agent.seo_agent_enabled ? "1" : "0"} onChange={(e) => saveAgent({ seo_agent_enabled: e.target.value === "1" })} className={adminInput} data-testid="agent-enabled">
                <option value="1">Attivo</option>
                <option value="0">Disattivo</option>
              </select>
            </AdminField>
            <AdminField label="Frequenza (giorni)">
              <select value={agent.seo_agent_frequency_days} onChange={(e) => saveAgent({ seo_agent_frequency_days: Number(e.target.value) })} className={adminInput} data-testid="agent-frequency">
                {[5, 7, 10, 14].map((d) => (<option key={d} value={d}>{`${d} giorni`}</option>))}
              </select>
            </AdminField>
            <AdminField label="Lunghezza articoli">
              <select value={agent.seo_agent_length} onChange={(e) => saveAgent({ seo_agent_length: e.target.value })} className={adminInput}>
                <option value="short">Breve</option>
                <option value="standard">Standard</option>
                <option value="deep">Approfondito</option>
              </select>
            </AdminField>
            <AdminField label="Territorio">
              <input defaultValue={agent.seo_agent_territory} onBlur={(e) => saveAgent({ seo_agent_territory: e.target.value })} className={adminInput} />
            </AdminField>
          </div>
          <dl className="mt-5 grid gap-x-8 gap-y-3 text-sm sm:grid-cols-2" data-testid="agent-schedule">
            <div>
              <dt className="text-xs uppercase tracking-[0.14em] text-wood/50">Ultima generazione riuscita</dt>
              <dd className="mt-1 text-foreground/75" data-testid="schedule-last-success">
                {fmtDateTime(agent.schedule?.last_success_at) || "nessuna ancora"}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-[0.14em] text-wood/50">Scadenza intervallo</dt>
              <dd className="mt-1 text-foreground/75" data-testid="schedule-interval-expires">
                {fmtDateTime(agent.schedule?.interval_expires_at) || "già scaduto"}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-[0.14em] text-wood/50">Prossimo controllo automatico</dt>
              <dd className="mt-1 text-foreground/75" data-testid="schedule-next-check">
                {fmtDateTime(agent.schedule?.next_cron_check_at)}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-[0.14em] text-wood/50">Prossima generazione automatica stimata</dt>
              <dd className="mt-1 text-foreground/75" data-testid="schedule-next-generation">
                {fmtDateTime(agent.schedule?.next_generation_at)}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-[0.14em] text-wood/50">Frequenza attiva</dt>
              <dd className="mt-1 text-foreground/75" data-testid="schedule-frequency">
                {`ogni ${agent.schedule?.frequency_days || agent.seo_agent_frequency_days} giorni`}
              </dd>
            </div>
          </dl>
          <p className="mt-3 text-xs text-foreground/50">
            Orari in ora locale. Il controllo automatico gira ogni giorno alle 08:00 (06:00 UTC): la bozza
            viene creata al primo controllo successivo alla scadenza dell'intervallo, negli altri giorni
            l'esecuzione è saltata senza nessuna chiamata AI.
          </p>
          <p className="mt-4 text-sm text-foreground/65">
            Pubblicazione automatica: <strong>disattivata</strong>. L'agente crea solo bozze da approvare.
          </p>
          <p className={`mt-2 text-sm ${agent.web_search_configured ? "text-sage" : "text-destructive"}`} data-testid="agent-search-status">
            Ricerca web: {agent.web_search_configured ? "configurata (Tavily)" : "NON configurata — serve TAVILY_API_KEY, senza la quale l'agente si ferma e non genera articoli"}
          </p>
        </AdminCard>
      )}

      {editing && (
        <Editor
          article={editing}
          onCancel={() => setEditing(null)}
          onEnrich={enrich}
          onImageSearch={imageSearch}
          onSaved={(status) => { setEditing(null); if (status) setTab(status); load(); }}
        />
      )}

      <div className="flex flex-wrap gap-5 border-b border-border pb-3">
        {STATUSES.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`articles-tab-${k}`}
            className={`text-[0.65rem] uppercase tracking-[0.16em] ${tab === k ? "text-sage" : "text-wood/50 hover:text-wood"}`}>
            {label}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title="Nessun articolo"
          description={
            tab === "draft"
              ? "Le bozze generate dall'agente compaiono qui."
              : tab === "published"
                ? "Nessun articolo pubblicato: apri una bozza e premi Pubblica."
                : tab === "scheduled"
                  ? "Nessun articolo programmato."
                  : "Nessun articolo archiviato."
          }
          testId="articles-empty"
        />
      ) : (
        <ul className="space-y-2">
          {rows.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center gap-4 border border-border bg-cream px-4 py-3" data-testid={`article-${a.slug}`}>
              <div className="min-w-0 flex-1">
                <p className="font-serif text-lg text-wood truncate">{a.title}</p>
                <p className="text-xs text-foreground/50">
                  {a.category_name} · {a.source_type === "agent" ? "generato dall'agente" : "manuale"}
                  {a.primary_keyword ? ` · ${a.primary_keyword}` : ""}
                  {a.scientific_sources?.length > 0 ? ` · ${a.scientific_sources.length} fonti scientifiche` : ""}
                </p>
                {!a.hero_image && (
                  <p className="mt-1 text-xs text-gold" data-testid={`article-needs-image-${a.slug}`}>Immagine da aggiungere</p>
                )}
              </div>
              <AdminButton variant="ghost" onClick={() => setEditing(a)} data-testid={`article-edit-${a.slug}`}>Apri</AdminButton>
              <AdminButton variant="danger" onClick={() => remove(a.id)} data-testid={`article-delete-${a.slug}`}><Trash2 size={13} /></AdminButton>
            </li>
          ))}
        </ul>
      )}

      <AdminCard title="Attività SEO agent" testId="agent-history">
        {runs.length === 0 ? (
          <p className="text-sm text-foreground/55">Nessuna esecuzione registrata.</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {runs.map((r) => (
              <li key={r.id} className="border border-border px-4 py-3">
                <p className="text-wood">
                  {new Date(r.started_at).toLocaleString("it-IT")} · {r.trigger} ·{" "}
                  <span className={r.status === "done" ? "text-sage" : r.status === "error" ? "text-destructive" : r.status === "skipped" ? "text-gold" : "text-wood/60"}>
                    {r.status === "done"
                      ? "completata"
                      : r.status === "error"
                        ? "errore"
                        : r.status === "skipped"
                          ? "saltata"
                          : "in corso"}
                  </span>
                </p>
                {r.note && <p className="mt-1 text-xs text-foreground/60">{r.note}</p>}
                {r.article_title && <p className="mt-1 text-foreground/70">Bozza: {r.article_title} — keyword: {r.selected_keyword}</p>}
                {r.error && <p className="mt-1 text-destructive">{r.error}</p>}
                {r.sources?.length > 0 && <p className="mt-1 text-xs text-foreground/50">{r.sources.length} fonti consultate</p>}
              </li>
            ))}
          </ul>
        )}
      </AdminCard>
    </div>
  );
}
